import classNames from 'classnames';
import {connect} from 'react-redux';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';
import PropTypes from 'prop-types';
import bindAll from 'lodash.bindall';
import React from 'react';
import io from 'socket.io-client';

import Box from '../box/box.jsx';
import Button from '../button/button.jsx';
import {ComingSoonTooltip} from '../coming-soon/coming-soon.jsx';
import Divider from '../divider/divider.jsx';
import LanguageSelector from '../../containers/language-selector.jsx';
import SaveStatus from './save-status.jsx';
import SBFileUploader from '../../containers/sb-file-uploader.jsx';
import MenuBarMenu from './menu-bar-menu.jsx';
import {MenuItem, MenuSection} from '../menu/menu.jsx';
import AccountNav from '../../containers/account-nav.jsx';
import LoginDropdown from './login-dropdown.jsx';
import SB3Downloader from '../../containers/sb3-downloader.jsx';
import DeletionRestorer from '../../containers/deletion-restorer.jsx';
import TurboMode from '../../containers/turbo-mode.jsx';
import RbSerialConnectionModal from '../../containers/rb-serial-connection-modal.jsx';
import RbFactoryReset from '../../containers/rb-factory-reset.jsx';
import RbFirmwareModal from '../../containers/rb-firmware-modal.jsx';
import RbUploadingModal from '../../containers/rb-uploading-modal.jsx';
import RbUploadFailureModal from '../../containers/rb-upload-failure-modal.jsx';
import RbUploadSuccessModal from '../../containers/rb-upload-success-modal.jsx';
import RbAccountModal from '../../containers/rb-account-modal.jsx';
import {updateStatus, updateVersion} from '../../reducers/connection';
import {openTipsLibrary, openSerialConnectionModal, openAccountModal} from '../../reducers/modals';
import {setPlayer} from '../../reducers/mode';
import {config} from '../../config';
import {
    autoUpdateProject,
    getIsUpdating,
    getIsShowingProject,
    manualUpdateProject,
    requestNewProject,
    remixProject,
    saveProjectAsCopy
} from '../../reducers/project-state';
import {
    openAccountMenu,
    closeAccountMenu,
    accountMenuOpen,
    openFileMenu,
    closeFileMenu,
    fileMenuOpen,
    openEditMenu,
    closeEditMenu,
    editMenuOpen,
    openLanguageMenu,
    closeLanguageMenu,
    languageMenuOpen,
    openLoginMenu,
    closeLoginMenu,
    loginMenuOpen,
    connectedMenuOpen,
    openConnectedMenu,
    closeConnectedMenu,
    accountMyQodeMenuOpen,
    openAccountMyQodeMenu,
    closeAccountMyQodeMenu
} from '../../reducers/menus';
import {updateRecord} from '../../reducers/account.js';

import styles from './menu-bar.css';

import helpIcon from '../../lib/assets/icon--tutorials.svg';
import mystuffIcon from './icon--mystuff.png';
import remixIcon from './icon--remix.svg';
import dropdownCaret from './dropdown-caret.svg';
import languageIcon from '../language-selector/language-icon.svg';

import scratchLogo from './ted-logo.svg';
import noConnectedIcon from './ico_usb_0.svg';
import yesConnectedIcon from './ico_usb_1.svg';
import downloadIcon from './icon-download.svg';
import userIcon from './icon_user.svg';

const messages = defineMessages({
    confirmNav: {
        id: 'gui.menuBar.confirmNewWithoutSaving',
        defaultMessage: 'Replace contents of the current project?',
        description: 'message for prompting user to confirm that they want to create new project without saving'
    }
});
const ariaMessages = defineMessages({
    language: {
        id: 'gui.menuBar.LanguageSelector',
        defaultMessage: 'language selector',
        description: 'accessibility text for the language selection menu'
    },
    tutorials: {
        id: 'gui.menuBar.tutorialsLibrary',
        defaultMessage: 'Tutorials',
        description: 'accessibility text for the tutorials button'
    }
});

const MenuBarItemTooltip = ({
    children,
    className,
    enable,
    id,
    place = 'bottom'
}) => {
    if (enable) {
        return (
            <React.Fragment>
                {children}
            </React.Fragment>
        );
    }
    return (
        <ComingSoonTooltip
            className={classNames(styles.comingSoon, className)}
            place={place}
            tooltipClassName={styles.comingSoonTooltip}
            tooltipId={id}
        >
            {children}
        </ComingSoonTooltip>
    );
};


MenuBarItemTooltip.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    enable: PropTypes.bool,
    id: PropTypes.string,
    place: PropTypes.oneOf(['top', 'bottom', 'left', 'right'])
};

const MenuItemTooltip = ({id, isRtl, children, className}) => (
    <ComingSoonTooltip
        className={classNames(styles.comingSoon, className)}
        isRtl={isRtl}
        place={isRtl ? 'left' : 'right'}
        tooltipClassName={styles.comingSoonTooltip}
        tooltipId={id}
    >
        {children}
    </ComingSoonTooltip>
);

MenuItemTooltip.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    id: PropTypes.string,
    isRtl: PropTypes.bool
};

class MenuBar extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleClickNew',
            'handleClickRemix',
            'handleClickSave',
            'handleClickSaveAsCopy',
            'handleClickSeeCommunity',
            'handleClickShare',
            'handleCloseFileMenuAndThen',
            'handleLanguageMouseUp',
            'handleRestoreOption',
            'restoreOptionMessage',
            'handleDisconnectSerial',
            'handleAccountLogout'
        ]);
        this.socket = io(config.socketUrl);
    }
    handleClickNew () {
        // if canSave===true and canCreateNew===true, it's safe to replace current project,
        // since we will auto-save first. Else, confirm first.
        const readyToReplaceProject = (this.props.canSave && this.props.canCreateNew) ||
            confirm(this.props.intl.formatMessage(messages.confirmNav)); // eslint-disable-line no-alert
        this.props.onRequestCloseFile();
        if (readyToReplaceProject) {
            this.props.onClickNew(this.props.canSave && this.props.canCreateNew);
        }
        this.props.onRequestCloseFile();
    }
    handleClickRemix () {
        this.props.onClickRemix();
        this.props.onRequestCloseFile();
    }
    handleClickSave () {
        this.props.onClickSave();
        this.props.onRequestCloseFile();
    }
    handleClickSaveAsCopy () {
        this.props.onClickSaveAsCopy();
        this.props.onRequestCloseFile();
    }
    handleClickSeeCommunity (waitForUpdate) {
        if (this.props.canSave) { // save before transitioning to project page
            this.props.autoUpdateProject();
            waitForUpdate(true); // queue the transition to project page
        } else {
            waitForUpdate(false); // immediately transition to project page
        }
    }
    handleClickShare (waitForUpdate) {
        if (!this.props.isShared) {
            if (this.props.canShare) { // save before transitioning to project page
                this.props.onShare();
            }
            if (this.props.canSave) { // save before transitioning to project page
                this.props.autoUpdateProject();
                waitForUpdate(true); // queue the transition to project page
            } else {
                waitForUpdate(false); // immediately transition to project page
            }
        }
    }
    handleRestoreOption (restoreFun) {
        return () => {
            restoreFun();
            this.props.onRequestCloseEdit();
        };
    }
    handleCloseFileMenuAndThen (fn) {
        return () => {
            this.props.onRequestCloseFile();
            fn();
        };
    }
    handleLanguageMouseUp (e) {
        if (!this.props.languageMenuOpen) {
            this.props.onClickLanguage(e);
        }
    }
    restoreOptionMessage (deletedItem) {
        switch (deletedItem) {
        case 'Sprite':
            return (<FormattedMessage
                defaultMessage="Restore Sprite"
                description="Menu bar item for restoring the last deleted sprite."
                id="gui.menuBar.restoreSprite"
            />);
        case 'Sound':
            return (<FormattedMessage
                defaultMessage="Restore Sound"
                description="Menu bar item for restoring the last deleted sound."
                id="gui.menuBar.restoreSound"
            />);
        case 'Costume':
            return (<FormattedMessage
                defaultMessage="Restore Costume"
                description="Menu bar item for restoring the last deleted costume."
                id="gui.menuBar.restoreCostume"
            />);
        default: {
            return (<FormattedMessage
                defaultMessage="Restore"
                description="Menu bar item for restoring the last deleted item in its disabled state." /* eslint-disable-line max-len */
                id="gui.menuBar.restore"
            />);
        }
        }
    }
    // 串口连接断开
    handleDisconnectSerial () {
        this.props.onUpdateStatus(false);
        this.props.onUpdateVersion('0');
        this.props.onRequestCloseConnected();
        this.socket.emit('serialDisconnect');
    }
    handleDownloadDrivers () {
        window.open('https://static.robobloq.cn/apps/pc/drivers/MyQode_Drivers.zip');
    }
    // 处理下载插件
    handleDownloadAgent () {
        const os = navigator.platform;
        if (os.includes('Mac')) {
            window.open('https://static.robobloq.cn/apps/pc/agent/Robobloq%20agent-1.0.4.dmg');
        } else {
            window.open('https://static.robobloq.cn/apps/pc/agent/Robobloq%20agent-Setup-1.0.4.exe');
        }
    }
    // 退出登录
    handleAccountLogout () {
        const record = '';
        this.props.onUpdateAccountRecord(record);
    }
    render () {
        const saveNowMessage = (
            <FormattedMessage
                defaultMessage="Save now"
                description="Menu bar item for saving now"
                id="gui.menuBar.saveNow"
            />
        );
        const createCopyMessage = (
            <FormattedMessage
                defaultMessage="Save as a copy"
                description="Menu bar item for saving as a copy"
                id="gui.menuBar.saveAsCopy"
            />
        );
        const remixMessage = (
            <FormattedMessage
                defaultMessage="Remix"
                description="Menu bar item for remixing"
                id="gui.menuBar.remix"
            />
        );
        const newProjectMessage = (
            <FormattedMessage
                defaultMessage="New"
                description="Menu bar item for creating a new project"
                id="gui.menuBar.new"
            />
        );
        const notConnected = (
            <FormattedMessage
                defaultMessage="Not Connected"
                description=""
                id="rb.gui.menuBar.notConnected"
            />
        );
        const drivers = (
            <FormattedMessage
                defaultMessage="Drivers"
                description=""
                id="rb.gui.menuBar.drivers"
            />
        );
        const plugins = (
            <FormattedMessage
                defaultMessage="Plugins"
                description=""
                id="rb.gui.menuBar.plugins"
            />
        );
        const connected = (
            <FormattedMessage
                defaultMessage="Connected"
                description=""
                id="rb.gui.menuBar.connected"
            />
        );
        const disconnect = (
            <FormattedMessage
                defaultMessage="Disconnect"
                description=""
                id="rb.gui.menuBar.disconnect"
            />
        );
        const factoryReset = (
            <FormattedMessage
                defaultMessage="Factory Reset"
                description=""
                id="rb.gui.menuBar.factoryReset"
            />
        );
        const account = (
            <FormattedMessage
                defaultMessage="Account"
                description=""
                id="rb.gui.menuBar.account"
            />
        );

        return (
            <Box
                className={classNames(
                    this.props.className,
                    styles.menuBar
                )}
            >
                <div className={styles.mainMenu}>
                    <div className={styles.fileGroup}>
                        <div className={classNames(styles.menuBarItem)}>
                            <a href="https://www.robobloq.com">
                                <img
                                    alt="Scratch"
                                    className={styles.scratchLogo}
                                    draggable={false}
                                    src={scratchLogo}
                                />
                            </a>
                        </div>
                        <div
                            className={classNames(styles.menuBarItem, styles.hoverable, styles.languageMenu)}
                        >
                            <div>
                                <img
                                    className={styles.languageIcon}
                                    src={languageIcon}
                                />
                                <img
                                    className={styles.languageCaret}
                                    src={dropdownCaret}
                                />
                            </div>
                            <LanguageSelector label={this.props.intl.formatMessage(ariaMessages.language)} />
                        </div>
                        <div
                            className={classNames(styles.menuBarItem, styles.hoverable, {
                                [styles.active]: this.props.fileMenuOpen
                            })}
                            onMouseUp={this.props.onClickFile}
                        >
                            <FormattedMessage
                                defaultMessage="File"
                                description="Text for file dropdown menu"
                                id="gui.menuBar.file"
                            />
                            <MenuBarMenu
                                className={classNames(styles.menuBarMenu)}
                                open={this.props.fileMenuOpen}
                                place={this.props.isRtl ? 'left' : 'right'}
                                onRequestClose={this.props.onRequestCloseFile}
                            >
                                <MenuSection>
                                    <MenuItem
                                        isRtl={this.props.isRtl}
                                        onClick={this.handleClickNew}
                                    >
                                        {newProjectMessage}
                                    </MenuItem>
                                </MenuSection>
                                <MenuSection>
                                    {this.props.canSave ? (
                                        <MenuItem onClick={this.handleClickSave}>
                                            {saveNowMessage}
                                        </MenuItem>
                                    ) : []}
                                    {this.props.canCreateCopy ? (
                                        <MenuItem onClick={this.handleClickSaveAsCopy}>
                                            {createCopyMessage}
                                        </MenuItem>
                                    ) : []}
                                    {this.props.canRemix ? (
                                        <MenuItem onClick={this.handleClickRemix}>
                                            {remixMessage}
                                        </MenuItem>
                                    ) : []}
                                </MenuSection>
                                <MenuSection>
                                    <SBFileUploader onUpdateProjectTitle={this.props.onUpdateProjectTitle}>
                                        {(className, renderFileInput, loadProject) => (
                                            <MenuItem
                                                className={className}
                                                onClick={loadProject}
                                            >
                                                <FormattedMessage
                                                    defaultMessage="Load from your computer"
                                                    description={
                                                        'Menu bar item for uploading a project from your computer'
                                                    }
                                                    id="gui.menuBar.uploadFromComputer"
                                                />
                                                {renderFileInput()}
                                            </MenuItem>
                                        )}
                                    </SBFileUploader>
                                    <SB3Downloader>{(className, downloadProject) => (
                                        <MenuItem
                                            className={className}
                                            onClick={this.handleCloseFileMenuAndThen(downloadProject)}
                                        >
                                            <FormattedMessage
                                                defaultMessage="Save to your computer"
                                                description="Menu bar item for downloading a project to your computer"
                                                id="gui.menuBar.downloadToComputer"
                                            />
                                        </MenuItem>
                                    )}</SB3Downloader>
                                </MenuSection>
                            </MenuBarMenu>
                        </div>
                        <div
                            className={classNames(styles.menuBarItem, styles.hoverable, {
                                [styles.active]: this.props.editMenuOpen
                            })}
                            onMouseUp={this.props.onClickEdit}
                        >
                            <div className={classNames(styles.editMenu)}>
                                <FormattedMessage
                                    defaultMessage="Edit"
                                    description="Text for edit dropdown menu"
                                    id="gui.menuBar.edit"
                                />
                            </div>
                            <MenuBarMenu
                                className={classNames(styles.menuBarMenu)}
                                open={this.props.editMenuOpen}
                                place={this.props.isRtl ? 'left' : 'right'}
                                onRequestClose={this.props.onRequestCloseEdit}
                            >
                                <DeletionRestorer>{(handleRestore, {restorable, deletedItem}) => (
                                    <MenuItem
                                        className={classNames({[styles.disabled]: !restorable})}
                                        onClick={this.handleRestoreOption(handleRestore)}
                                    >
                                        {this.restoreOptionMessage(deletedItem)}
                                    </MenuItem>
                                )}</DeletionRestorer>
                                <MenuSection>
                                    <TurboMode>{(toggleTurboMode, {turboMode}) => (
                                        <MenuItem onClick={toggleTurboMode}>
                                            {turboMode ? (
                                                <FormattedMessage
                                                    defaultMessage="Turn off Turbo Mode"
                                                    description="Menu bar item for turning off turbo mode"
                                                    id="gui.menuBar.turboModeOff"
                                                />
                                            ) : (
                                                <FormattedMessage
                                                    defaultMessage="Turn on Turbo Mode"
                                                    description="Menu bar item for turning on turbo mode"
                                                    id="gui.menuBar.turboModeOn"
                                                />
                                            )}
                                        </MenuItem>
                                    )}</TurboMode>
                                </MenuSection>
                            </MenuBarMenu>
                        </div>
                    </div>
                    <Divider className={classNames(styles.divider)} />
                    <div
                        aria-label={this.props.intl.formatMessage(ariaMessages.tutorials)}
                        className={classNames(styles.menuBarItem, styles.hoverable)}
                        onClick={this.props.onOpenTipLibrary}
                    >
                        <img
                            className={styles.helpIcon}
                            src={helpIcon}
                        />
                        <FormattedMessage {...ariaMessages.tutorials} />
                    </div>
                </div>

                {/* show the proper UI in the account menu, given whether the user is
                logged in, and whether a session is available to log in with */}
                <div className={styles.accountInfoGroup}>
                    <div className={styles.menuBarItem}>
                        {this.props.canSave && (
                            <SaveStatus />
                        )}
                    </div>
                    {this.props.sessionExists ? (
                        this.props.username ? (
                            // ************ user is logged in ************
                            <React.Fragment>
                                <a href="/mystuff/">
                                    <div
                                        className={classNames(
                                            styles.menuBarItem,
                                            styles.hoverable,
                                            styles.mystuffButton
                                        )}
                                    >
                                        <img
                                            className={styles.mystuffIcon}
                                            src={mystuffIcon}
                                        />
                                    </div>
                                </a>
                                <AccountNav
                                    className={classNames(
                                        styles.menuBarItem,
                                        styles.hoverable,
                                        {[styles.active]: this.props.accountMenuOpen}
                                    )}
                                    isOpen={this.props.accountMenuOpen}
                                    isRtl={this.props.isRtl}
                                    menuBarMenuClassName={classNames(styles.menuBarMenu)}
                                    onClick={this.props.onClickAccount}
                                    onClose={this.props.onRequestCloseAccount}
                                    onLogOut={this.props.onLogOut}
                                />
                            </React.Fragment>
                        ) : (
                            // ********* user not logged in, but a session exists
                            // ********* so they can choose to log in
                            <React.Fragment>
                                <div
                                    className={classNames(
                                        styles.menuBarItem,
                                        styles.hoverable
                                    )}
                                    key="join"
                                    onMouseUp={this.props.onOpenRegistration}
                                >
                                    <FormattedMessage
                                        defaultMessage="Join Scratch"
                                        description="Link for creating a Scratch account"
                                        id="gui.menuBar.joinScratch"
                                    />
                                </div>
                                <div
                                    className={classNames(
                                        styles.menuBarItem,
                                        styles.hoverable
                                    )}
                                    key="login"
                                    onMouseUp={this.props.onClickLogin}
                                >
                                    <FormattedMessage
                                        defaultMessage="Sign in"
                                        description="Link for signing in to your Scratch account"
                                        id="gui.menuBar.signIn"
                                    />
                                    <LoginDropdown
                                        className={classNames(styles.menuBarMenu)}
                                        isOpen={this.props.loginMenuOpen}
                                        isRtl={this.props.isRtl}
                                        renderLogin={this.props.renderLogin}
                                        onClose={this.props.onRequestCloseLogin}
                                    />
                                </div>
                            </React.Fragment>
                        )
                    ) : (
                        // ******** no login session is available, so don't show login stuff
                        <React.Fragment>
                            <React.Fragment>
                                {
                                    <div
                                        className={classNames(styles.menuBarItem, styles.hoverable)}
                                        onClick={this.handleDownloadDrivers}
                                    >
                                        <img
                                            className={styles.iconImg}
                                            src={downloadIcon}
                                        />
                                        {drivers}
                                    </div>
                                }
                            </React.Fragment>
                            <React.Fragment>
                                {
                                    <div
                                        className={classNames(styles.menuBarItem, styles.hoverable)}
                                        onClick={this.handleDownloadAgent}
                                    >
                                        <img
                                            className={styles.iconImg}
                                            src={downloadIcon}
                                        />
                                        {plugins}
                                    </div>
                                }
                            </React.Fragment>
                            {this.props.showComingSoon ? (
                                <React.Fragment>
                                    {
                                        this.props.connectionStatus ? (
                                            <div
                                                className={classNames(styles.menuBarItem, styles.hoverable, {
                                                    [styles.active]: this.props.connectedMenuOpen
                                                })}
                                                onMouseUp={this.props.onClickConnected}
                                            >
                                                <img
                                                    className={styles.iconImg}
                                                    src={yesConnectedIcon}
                                                />
                                                {connected}
                                                <MenuBarMenu
                                                    className={classNames(styles.menuBarMenu)}
                                                    open={this.props.connectedMenuOpen}
                                                    place={'left'}
                                                    onRequestClose={this.props.onRequestCloseConnected}
                                                >
                                                    <MenuSection>
                                                        <RbFactoryReset>{
                                                            (onRequestOpenFirmware, firmwareVisible, firmwareProps) => (
                                                                <MenuItem
                                                                    onClick={onRequestOpenFirmware}
                                                                    {...firmwareProps}
                                                                >
                                                                    {factoryReset}
                                                                </MenuItem>
                                                            )
                                                        }</RbFactoryReset>
                                                    </MenuSection>
                                                    <MenuSection>
                                                        <MenuItem
                                                            isRtl={this.props.isRtl}
                                                            onClick={this.handleDisconnectSerial}
                                                        >
                                                            {disconnect}
                                                        </MenuItem>
                                                    </MenuSection>
                                                </MenuBarMenu>
                                            </div>
                                        ) : (
                                            <div
                                                className={classNames(styles.menuBarItem, styles.hoverable)}
                                                onClick={this.props.onOpenSerialConnectionModal}
                                            >
                                                <img
                                                    className={styles.iconImg}
                                                    src={noConnectedIcon}
                                                />
                                                {notConnected}
                                                {
                                                    this.props.isShowSerialConnectionModal ? (
                                                        <RbSerialConnectionModal />
                                                    ) : ''
                                                }
                                            </div>
                                        )
                                    }

                                    {/* {
                                        this.props.accountRecord ? (
                                            <div
                                                className={classNames(styles.menuBarItem, styles.hoverable, {
                                                    [styles.active]: this.props.accountMyQodeMenuOpen
                                                })}
                                                onMouseUp={this.props.onClickAccountMyQode}
                                            >
                                                <img
                                                    className={styles.iconImg}
                                                    src={userIcon}
                                                />
                                                {JSON.parse(this.props.accountRecord).user.username}
                                                <MenuBarMenu
                                                    className={classNames(styles.menuBarMenu)}
                                                    open={this.props.accountMyQodeMenuOpen}
                                                    place={'left'}
                                                    onRequestClose={this.props.onRequestCloseAccountMyQode}
                                                >
                                                    <MenuSection>
                                                        <MenuItem
                                                            isRtl={this.props.isRtl}
                                                            onClick={this.handleAccountLogout}
                                                        >
                                                            {'Logout'}
                                                        </MenuItem>
                                                    </MenuSection>
                                                </MenuBarMenu>
                                            </div>
                                        ) : (
                                            <div
                                                className={classNames(styles.menuBarItem, styles.hoverable)}
                                                onClick={this.props.onOpenAccountModal}
                                            >
                                                <img
                                                    className={styles.iconImg}
                                                    src={userIcon}
                                                />
                                                {account}
                                                {
                                                    this.props.isShowAccountModal ? (
                                                        <RbAccountModal />
                                                    ) : ''
                                                }
                                            </div>
                                        )
                                    } */}
                                </React.Fragment>
                            ) : []}
                        </React.Fragment>
                    )}
                </div>
                {/* 恢复出厂相关弹窗 */}
                {this.props.firmwareVisible ? <RbFirmwareModal /> : ''}
                {this.props.uploadingModalStatus ? <RbUploadingModal /> : ''}
                {this.props.uploadFailureModalStatus ? <RbUploadFailureModal /> : null}
                {this.props.uploadSuccessModalStatus ? <RbUploadSuccessModal /> : null}
            </Box>
        );
    }
}

MenuBar.propTypes = {
    accountMenuOpen: PropTypes.bool,
    accountMyQodeMenuOpen: PropTypes.bool,
    accountRecord: PropTypes.string.isRequired,
    authorId: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    authorThumbnailUrl: PropTypes.string,
    authorUsername: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    autoUpdateProject: PropTypes.func,
    canCreateCopy: PropTypes.bool,
    canCreateNew: PropTypes.bool,
    canEditTitle: PropTypes.bool,
    canRemix: PropTypes.bool,
    canSave: PropTypes.bool,
    canShare: PropTypes.bool,
    className: PropTypes.string,
    connectedMenuOpen: PropTypes.bool,
    connectionStatus: PropTypes.bool,
    editMenuOpen: PropTypes.bool,
    enableCommunity: PropTypes.bool,
    fileMenuOpen: PropTypes.bool,
    firmwareVisible: PropTypes.bool,
    intl: intlShape,
    isRtl: PropTypes.bool,
    isShared: PropTypes.bool,
    isShowSerialConnectionModal: PropTypes.bool,
    isShowingProject: PropTypes.bool,
    isUpdating: PropTypes.bool,
    languageMenuOpen: PropTypes.bool,
    loginMenuOpen: PropTypes.bool,
    onClickAccount: PropTypes.func,
    onClickConnected: PropTypes.func,
    onClickEdit: PropTypes.func,
    onClickFile: PropTypes.func,
    onClickLanguage: PropTypes.func,
    onClickLogin: PropTypes.func,
    onClickNew: PropTypes.func,
    onClickRemix: PropTypes.func,
    onClickSave: PropTypes.func,
    onClickSaveAsCopy: PropTypes.func,
    onLogOut: PropTypes.func,
    onOpenRegistration: PropTypes.func,
    onOpenSerialConnectionModal: PropTypes.func,
    onOpenTipLibrary: PropTypes.func,
    onRequestCloseAccount: PropTypes.func,
    onRequestCloseConnected: PropTypes.func,
    onRequestCloseEdit: PropTypes.func,
    onRequestCloseFile: PropTypes.func,
    onRequestCloseLanguage: PropTypes.func,
    onRequestCloseLogin: PropTypes.func,
    onSeeCommunity: PropTypes.func,
    onShare: PropTypes.func,
    onToggleLoginOpen: PropTypes.func,
    onUpdateProjectTitle: PropTypes.func,
    onUpdateStatus: PropTypes.func,
    projectTitle: PropTypes.string,
    renderLogin: PropTypes.func,
    sessionExists: PropTypes.bool,
    showComingSoon: PropTypes.bool,
    uploadFailureModalStatus: PropTypes.bool,
    uploadSuccessModalStatus: PropTypes.bool,
    uploadingModalStatus: PropTypes.bool,
    username: PropTypes.string
};

MenuBar.defaultProps = {
    onShare: () => {}
};

const mapStateToProps = state => {
    const loadingState = state.scratchGui.projectState.loadingState;
    const user = state.session && state.session.session && state.session.session.user;
    return {
        accountRecord: state.scratchGui.account.record,
        accountMenuOpen: accountMenuOpen(state),
        connectionStatus: state.scratchGui.connection.status,
        connectedMenuOpen: connectedMenuOpen(state),
        accountMyQodeMenuOpen: accountMyQodeMenuOpen(state),
        fileMenuOpen: fileMenuOpen(state),
        firmwareVisible: state.scratchGui.modals.firmware,
        editMenuOpen: editMenuOpen(state),
        isRtl: state.locales.isRtl,
        isUpdating: getIsUpdating(loadingState),
        isShowingProject: getIsShowingProject(loadingState),
        isShowSerialConnectionModal: state.scratchGui.modals.serialConnectionModal,
        isShowAccountModal: state.scratchGui.modals.accountModal,
        languageMenuOpen: languageMenuOpen(state),
        loginMenuOpen: loginMenuOpen(state),
        projectTitle: state.scratchGui.projectTitle,
        sessionExists: state.session && typeof state.session.session !== 'undefined',
        username: user ? user.username : null,
        uploadSuccessModalStatus: state.scratchGui.modals.uploadSuccess,
        uploadFailureModalStatus: state.scratchGui.modals.uploadFailure,
        uploadingModalStatus: state.scratchGui.modals.uploading
    };
};

const mapDispatchToProps = dispatch => ({
    autoUpdateProject: () => dispatch(autoUpdateProject()),
    onOpenTipLibrary: () => dispatch(openTipsLibrary()),
    onOpenSerialConnectionModal: () => dispatch(openSerialConnectionModal()),
    onOpenAccountModal: () => dispatch(openAccountModal()),
    onClickAccount: () => dispatch(openAccountMenu()),
    onRequestCloseAccount: () => dispatch(closeAccountMenu()),
    onClickFile: () => dispatch(openFileMenu()),
    onRequestCloseFile: () => dispatch(closeFileMenu()),
    onClickEdit: () => dispatch(openEditMenu()),
    onClickConnected: () => dispatch(openConnectedMenu()),
    onClickAccountMyQode: () => dispatch(openAccountMyQodeMenu()),
    onRequestCloseEdit: () => dispatch(closeEditMenu()),
    onClickLanguage: () => dispatch(openLanguageMenu()),
    onRequestCloseLanguage: () => dispatch(closeLanguageMenu()),
    onClickLogin: () => dispatch(openLoginMenu()),
    onRequestCloseLogin: () => dispatch(closeLoginMenu()),
    onRequestCloseConnected: () => dispatch(closeConnectedMenu()),
    onRequestCloseAccountMyQode: () => dispatch(closeAccountMyQodeMenu()),
    onClickNew: needSave => dispatch(requestNewProject(needSave)),
    onClickRemix: () => dispatch(remixProject()),
    onClickSave: () => dispatch(manualUpdateProject()),
    onClickSaveAsCopy: () => dispatch(saveProjectAsCopy()),
    onSeeCommunity: () => dispatch(setPlayer(true)),
    onUpdateStatus: status => {
        dispatch(updateStatus(status));
    },
    onUpdateVersion: status => {
        dispatch(updateVersion(status));
    },
    onUpdateAccountRecord: record => {
        dispatch(updateRecord(record));
    }
});

export default injectIntl(connect(
    mapStateToProps,
    mapDispatchToProps
)(MenuBar));
